import { useEffect } from "react";
import { useGame } from "../../../context/GameContext";
import "./promotionMenu.scss";
import { Color } from "chess.js";
import ChessButton from "../../visual/ChessButton/ChessButton";
import useSoundEffect from "../../../hooks/useSoundEffect";
import { KeyCodes } from "../../../utils/navigationHelpers";
import { useMultiplayer } from "../../../context/MultiplayerContext";
import { PromotionOption } from "../../../types/general";

export default function Promotion() {
    const { gameState, promotion, setPromotion } = useGame();
    const { playClickAudio } = useSoundEffect();
    const { multiplayerState } = useMultiplayer();

    /* If the user clicks outside the promotion dialog, close it. */
    useEffect(() => {
        // Only add the click handler when promotion dialog is actually open
        if (!promotion.open) return;

        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target) {
                console.error("Event target is null");
                return;
            }
            if (target.className === "overlay") {
                setPromotion({ open: false, type: "cancel" });
            }
        };

        window.addEventListener("click", handleClick);

        return () => {
            window.removeEventListener("click", handleClick);
        };
    }, [setPromotion, promotion.open]);

    useEffect(() => {
        if (!promotion.open) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (KeyCodes.Ups.includes(event.key) || KeyCodes.Downs.includes(event.key)) {
                playClickAudio();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [promotion, playClickAudio]);

    if (!promotion.open) {
        return null;
    }

    const promotionColor: Color | null = gameState.isAIGame ? "w" : multiplayerState.playerColor;

    if (!promotionColor) {
        console.error("Promotion color not set");
        return;
    }

    const onClick = (type: PromotionOption) => {
        setPromotion({ open: false, type });
    };

    return (
        <div className="overlay" data-testid="promotion-dialog">
            <div className="promotion-modal">
                <h1>Promote Pawn</h1>
                <div className="promotion-options">
                    <ChessButton
                        data-testid="queen-promotion"
                        data-navcol={0}
                        data-navrow={0}
                        onClick={() => onClick("q")}
                    >
                        Queen
                    </ChessButton>
                    <ChessButton
                        data-navcol={0}
                        data-navrow={1}
                        data-testid="rook-promotion"
                        onClick={() => onClick("r")}
                    >
                        Rook
                    </ChessButton>
                    <ChessButton
                        data-navcol={0}
                        data-navrow={2}
                        data-testid="bishop-promotion"
                        onClick={() => onClick("b")}
                    >
                        Bishop
                    </ChessButton>
                    <ChessButton
                        data-navcol={0}
                        data-navrow={3}
                        data-testid="knight-promotion"
                        onClick={() => onClick("n")}
                    >
                        Knight
                    </ChessButton>
                    <ChessButton
                        data-navcol={0}
                        data-navrow={4}
                        data-testid="cancel-promotion"
                        onClick={() => onClick("cancel")}
                    >
                        Cancel
                    </ChessButton>
                </div>
            </div>
        </div>
    );
}
